import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { UserPen } from 'lucide-react';
import { useStore } from '../../../../hooks/useStore';
import FormPopupFooter from '../../../../components/form-components/common-components/FormPopupFooter';
import { PersonSchema } from '../../../../schemas/PersonSchema';
import { PersonFormValues } from '../../../../models/person/PersonModel';
import { toast } from 'react-toastify';
import PersonFormFields from './PersonFormFields';
import { getEditPersonFormValues } from './personFormUtils';
import styles from './PersonForm.module.css';

const EditPersonPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore } = useStore();
  const selectedPerson = personStore.selectedPersonToEdit;

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isEditPersonOpen) {
        return;
      }

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };

    void fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isEditPersonOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const handleSubmit = async (values: PersonFormValues) => {
    if (!selectedPerson?.id) {
      toast.error('Kişi ID bulunamadı');
      return;
    }

    if (!values.hospitalId) {
      toast.error('Lütfen bir hastane seçiniz');
      return;
    }

    const response = await personStore.updatePerson(selectedPerson.id, values);
    if (response.error === false) {
      toast.success('Kişi başarıyla güncellendi.');
      popupStore.setIsEditPersonOpen(false);
      personStore.setSelectedPersonToEdit(null);
      personStore.refreshGrid();
      return;
    }

    toast.error('Kişi güncellenemedi. ' + response.message);
  };

  const handleClose = () => {
    popupStore.setIsEditPersonOpen(false);
    personStore.setSelectedPersonToEdit(null);
    toast.dismiss();
  };

  if (!popupStore.isEditPersonOpen || !selectedPerson) {
    return null;
  }

  return (
    <Formik
      initialValues={getEditPersonFormValues(selectedPerson)}
      validationSchema={PersonSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {() => (
        <Popup
          isOpen={popupStore.isEditPersonOpen}
          onClose={handleClose}
          title="Kişi Düzenle"
          description="Kişi bilgilerini güncelleyin"
          variant="success"
          icon={<UserPen size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter
              onClose={handleClose}
              saveLabel="Güncelle"
              submittingLabel="Güncelleniyor..."
            />
          )}
        >
          <Form className={styles.form}>
            <PersonFormFields hospitalOptions={hospitalOptions} />
          </Form>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditPersonPopup);
